import { useState, useEffect, useRef } from 'react'
import { Box, Fab } from '@mui/material'
import * as Icons from '@mui/icons-material'
import CropScheduleTimeline from '~/crop/crop-schedule/crop-timeline'
import { TimelineRowHelpers } from '~/crop/crop-schedule/crop-timeline/components/timeline-row-helpers'
import { CHROME_SCROLLBAR_HEIGHT } from '~/common/constants/layout-constants'
import { TIMELINE_PREVIEW_CONTAINER_HEIGHT, TIMELINE_PREVIEW_CONTENT_HEIGHT } from '../constants/crop-modal-constants'
import { TpTooltip } from '~/components'
import { useCropModalContext } from '../context/crop-modal-context'

const WEEK_MS = 7 * 24 * 60 * 60 * 1000
const PIXELS_PER_WEEK = 32 // WEEK_CONTAINER_FALLBACK_WIDTH — preview has no zoom controls
const ANIMATION_DURATION_MS = 400

const getEarliestPhaseMs = (phases = []) => {
  let ms = Infinity
  for (const phase of phases) {
    if (phase.est_started_at) {
      const d = new Date(phase.est_started_at).getTime()
      if (d < ms) ms = d
    }
  }
  return ms
}

const shiftPhases = (phases, deltaMs) =>
  phases.map(phase => ({
    ...phase,
    est_started_at: phase.est_started_at
      ? new Date(new Date(phase.est_started_at).getTime() + deltaMs).toISOString()
      : phase.est_started_at,
  }))

const shiftTasks = (tasks, deltaMs) =>
  tasks.map(task => ({
    ...task,
    task_window_start: task.task_window_start
      ? new Date(new Date(task.task_window_start).getTime() + deltaMs).toISOString()
      : task.task_window_start,
    task_window_end: task.task_window_end
      ? new Date(new Date(task.task_window_end).getTime() + deltaMs).toISOString()
      : task.task_window_end,
  }))

const getTodayMs = () => {
  const t = new Date()
  t.setHours(0, 0, 0, 0)
  return t.getTime()
}

export const CropTimelinePreview = ({
  previewScheduleData,
  timelineScrollConfig,
  adjustedPhases,
  adjustedTasks,
  selectedPhaseCodes,
  onPhaseAdjust,
  onTaskAdjust,
  onReset,
  frostDates,
}) => {
  const { growthType } = useCropModalContext()

  // Scroll freeze: prevent viewport auto-scroll during pan animation
  const [localScrollDate, setLocalScrollDate] = useState(() => timelineScrollConfig?.scrollToDate)
  const scrollLockedRef = useRef(false)
  useEffect(() => {
    function syncScrollDate() {
      if (!scrollLockedRef.current) {
        setLocalScrollDate(timelineScrollConfig?.scrollToDate)
      }
    }
    syncScrollDate()
  }, [timelineScrollConfig?.scrollToDate])

  // Pan animation trigger — reset to 0 after one RAF so the DOM animation runs exactly once
  const [panAnimationX, setPanAnimationX] = useState(0)
  useEffect(() => {
    if (panAnimationX !== 0) {
      const id = requestAnimationFrame(() => setPanAnimationX(0))
      return () => cancelAnimationFrame(id)
    }
  }, [panAnimationX])

  const isResetDisabled = !adjustedPhases && !adjustedTasks && selectedPhaseCodes === null

  const crop = previewScheduleData?.[0]
  const cropPhases = crop?.phases ?? []
  const cropTasks = crop?.tasks ?? []

  // Button enabled only when earliest phase start is before today (tasks are windows, not anchor points)
  const cropEarliestPhaseMs = getEarliestPhaseMs(cropPhases)
  const isJumpToNowEnabled = isFinite(cropEarliestPhaseMs) && cropEarliestPhaseMs < getTodayMs()

  const handleJumpToNow = () => {
    if (!crop || !isJumpToNowEnabled) return

    const earliestMs = getEarliestPhaseMs(cropPhases)
    if (!isFinite(earliestMs)) return

    const todayMs = getTodayMs()
    const deltaMs = todayMs - earliestMs
    if (deltaMs <= 0) return

    // Pixels the schedule bars will visually travel rightward
    const deltaXPixels = (deltaMs / WEEK_MS) * PIXELS_PER_WEEK

    // Shift all phases
    let shiftedPhases = shiftPhases(cropPhases, deltaMs)

    // Indeterminate: cap mature phase end at FFD by shrinking duration instead of extending past frost
    if (growthType === 'indeterminate' && frostDates?.ffd) {
      const ffd = new Date(frostDates.ffd)
      shiftedPhases = shiftedPhases.map(p => {
        if (p.phase_type_code === 'mature') {
          const matureStart = new Date(p.est_started_at)
          const daysToFFD = Math.max(1, Math.floor((ffd - matureStart) / (24 * 60 * 60 * 1000)))
          return { ...p, est_duration: `${daysToFFD} days` }
        }
        return p
      })
    }

    // Shift all tasks
    let shiftedTasks = shiftTasks(cropTasks, deltaMs)

    // Indeterminate: cap harvest task_window_end at FFD
    if (growthType === 'indeterminate' && frostDates?.ffd) {
      const ffd = new Date(frostDates.ffd)
      shiftedTasks = shiftedTasks.map(task => {
        if (task.task_code === 'harvest' && task.task_window_end) {
          return { ...task, task_window_end: ffd.toISOString() }
        }
        return task
      })
    }

    // Freeze viewport scroll during animation so the view doesn't jump
    scrollLockedRef.current = true
    setTimeout(() => { scrollLockedRef.current = false }, ANIMATION_DURATION_MS + 100)

    // Trigger animation: negative offset = bars appear at old position, then slide right to new position
    setPanAnimationX(-deltaXPixels)

    onPhaseAdjust(shiftedPhases)
    onTaskAdjust(shiftedTasks)
  }

  return (
    <Box
      sx={{
        height: TIMELINE_PREVIEW_CONTAINER_HEIGHT,
        '@media screen and (-webkit-min-device-pixel-ratio:0) and (min-resolution: .001dpcm)': {
          height: TIMELINE_PREVIEW_CONTAINER_HEIGHT + CHROME_SCROLLBAR_HEIGHT
        },
        '@supports (-webkit-touch-callout: none)': {
          height: TIMELINE_PREVIEW_CONTAINER_HEIGHT + CHROME_SCROLLBAR_HEIGHT
        }
      }}
    >
      <CropScheduleTimeline
        scheduleData={previewScheduleData?.slice(0, 1)}
        hideYAxis
        hidePhaseTooltip
        hideRowLines
        hideToggleButtons
        forceAllExpand
        contentHeight={TIMELINE_PREVIEW_CONTENT_HEIGHT}
        interactive
        dayPrecision
        viewStrategy='both'
        onPhaseAdjust={onPhaseAdjust}
        onTaskAdjust={onTaskAdjust}
        scrollToDate={localScrollDate}
        scrollToLeftOffset={timelineScrollConfig.scrollToLeftOffset}
        panAnimationX={panAnimationX}
        hideBadges
      >
        <TimelineRowHelpers />
      </CropScheduleTimeline>

      <Box sx={{ position: 'relative' }}>
        <TpTooltip title='Move entire schedule to start now'>
          <Fab
            size="small"
            color='primary'
            onClick={handleJumpToNow}
            disabled={!isJumpToNowEnabled}
            sx={{ position: 'absolute', bottom: 166, right: 12, boxShadow: 3, zIndex: 10 }}
          >
            <Icons.KeyboardTab />
          </Fab>
        </TpTooltip>

        <TpTooltip title='Reset to default'>
          <Fab
            size="small"
            color='primary'
            onClick={onReset}
            disabled={isResetDisabled}
            sx={{ position: 'absolute', bottom: 12, right: 12, boxShadow: 3, zIndex: 10 }}
          >
            <Icons.Restore />
          </Fab>
        </TpTooltip>
      </Box>
    </Box>
  )
}
